import React, { useState, useEffect } from 'react';
import { ChartControlsProps } from '../../redux/ChartPoperties/ChartControlsInterface';
import { connect } from 'react-redux';
import { formatChartLabelValueForSelectedMeasure } from '../ChartOptions/Format/NumberFormatter';
import { ChartsReduxStateProps } from './ChartsCommonInterfaces';

import { Dispatch } from 'redux';
import {
  addOrEditSimplecardConditionalFormat,
  updateCardControls,
} from '../../redux/ChartPoperties/ChartControlsActions';

import EmptyQuery from './EmptyQuery';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import { setDisplayName } from '../ChartAxes/setDisplayName';
import { setPrefix } from '../ChartAxes/SetPrefix';
import { shouldChartDisplayWithSecurity } from '../CommonFunctions/CommonFunctions';
import { checkIsConditionSatisfied } from '../ChartOptions/CommonComponents';

const SimpleCard = ({
  //props
  propKey,
  graphDimension,
  fontFamily,
  chartArea,
  graphTileSize,
  colorScheme,

  //state
  pageSettings,
  chartControls,
  chartProperties,
  tabtileProps,
  tileState,

  //dispatch
  updateCardControls,
  addOrEditSimplecardConditionalFormat,
}: ChartsReduxStateProps & any) => {
  var chartControl: ChartControlsProps = chartControls?.properties[propKey];
  let chartData: any[] = chartControl?.chartData ? chartControl.chartData : [];

  const [backgroundColorValue, setBackgroundColorValue] = useState<string | null>('');
  const [fontColor, setFontColor] = useState<string | null>('');
  const [italicText, setItalicText] = useState<string | null>('');
  const [boldText, setBoldText] = useState<string | null>('');
  const [textUnderline, setTextUnderline] = useState<string | null>('');
  const [cardData, setCardData] = useState<any[]>([]);

  var formats = chartControl?.simplecardConditionalFormats;

  useEffect(() => {
    if (formats?.length > 0) {
      for (let i = formats.length - 1; i >= 0; i--) {
        if (formats[i].isConditionSatisfied) {
          setBackgroundColorValue(formats[i].backgroundColor);
          setFontColor(formats[i].fontColor);
          setBoldText(formats[i].isBold ? 'bold' : 'normal');
          setItalicText(formats[i].isItalic ? 'italic' : 'normal');
          setTextUnderline(formats[i].isUnderlined ? 'underline' : 'none');
          return;
        }
        if (i === 0 && !formats[i].isConditionSatisfied) {
          setBackgroundColorValue(null);
          setFontColor(null);
          setBoldText(null);
          setItalicText(null);
          setTextUnderline(null);
        }
      }
    } else {
      setBackgroundColorValue(null);
      setFontColor(null);
      setBoldText(null);
      setItalicText(null);
      setTextUnderline(null);
    }
  }, [formats]);

  useEffect(() => {
    if (chartData.length >= 1 && chartControl) {
      setCardData(chartData[0][Object.keys(chartData[0])[0]]);
      if (chartControl.cardControls.generateLabelName !== 'Manual') {
        const measurefield = chartProperties.properties[propKey].chartAxes[1]?.fields[0];
        let subText = measurefield?.displayname ?? Object.keys(chartData[0])[0];
        if (measurefield?.isTextRenamed) {
          const newFieldData = setDisplayName(
            setPrefix(measurefield, 'Measure', 'simplecard'),
            'Measure',
            'simplecard',
          );
          updateCardControls(propKey, 'subText', newFieldData?.displayname ?? subText);
          return;
        }
        updateCardControls(propKey, 'subText', subText);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, chartControl?.cardControls?.generateLabelName]);

  const prevFieldIdRef = React.useRef<Record<string, string | null>>({});
  const isFieldChangedRef = React.useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (chartData.length < 1) return;

    const currentFieldId = chartProperties.properties[propKey].chartAxes[1]?.fields?.[0]?.uId || null;
    const prevFieldId = prevFieldIdRef.current[propKey];

    if (prevFieldId && prevFieldId !== currentFieldId) {
      isFieldChangedRef.current[propKey] = true;
      addOrEditSimplecardConditionalFormat(propKey, []);
      prevFieldIdRef.current[propKey] = currentFieldId;
      return;
    }

    prevFieldIdRef.current[propKey] = currentFieldId;

    if (isFieldChangedRef.current[propKey]) {
      isFieldChangedRef.current[propKey] = false;
      return;
    }

    const value = chartData[0];
    if (!value) return;

    const cfList = chartControls.properties[propKey].simplecardConditionalFormats;

    if (!cfList || cfList.length === 0) return;

    const valueName = Object.keys(value);
    const dataValue = value[valueName[0]];

    addOrEditSimplecardConditionalFormat(propKey, checkIsConditionSatisfied(cfList, dataValue));
  }, [chartData, chartProperties.properties[propKey].chartAxes]);

  const getFormatedChartData = () => {
    var formattedValue = cardData;

    if (formattedValue === null || formattedValue.length === 0) {
      return '(Blank)';
    }

    formattedValue = formatChartLabelValueForSelectedMeasure(
      chartControls.properties[propKey],
      chartProperties.properties[propKey],
      formattedValue,
      chartProperties.properties[propKey].chartAxes[
        chartProperties.properties[propKey].chartAxes.findIndex(
          (item: any) => item.name === 'Measure',
        )
      ]?.fields[0]?.displayname,
    );
    return formattedValue;
  };
  const allThemes = useCombinedColorSchemes();
  const chartThemes = allThemes.filter((el) => {
    if (
      !chartControl.colorScheme ||
      (chartArea === 'dashboard' && !chartControl.cardControls.manualColor)
    ) {
      return el.name === colorScheme;
    } else {
      return el.name === chartControl.colorScheme;
    }
  });
  const selectedTheme = chartThemes.length > 0 ? chartThemes[0] : allThemes[0];

  ////////// Use when enabling the dragging option
  // useEffect(() => {
  //   updateCardControls(propKey, "mainTextPos", {
  //     x: graphDimension.width / 3,
  //     y: graphDimension.height / 2 - graphDimension.height / 3,
  //   });
  //   updateCardControls(propKey, "subTextPos", {
  //     x: graphDimension.width / 3.2,
  //     y: graphDimension.height / 2 + graphDimension.height / 16,
  //   });
  // }, [
  //   chartControls.properties[propKey].serverData,
  //   pageSettings.fullScreen,
  //   graphDimension.width,
  //   graphDimension.height,
  //   chartControls.properties[propKey].cardControls.customStyle,
  // ]);

  const RenderChart = () => {
    return (
      <div
        style={{
          width: graphDimension.width,
          height: graphDimension.height,
          overflow: 'hidden',
          margin: 'auto',
          border: chartArea ? 'none' : graphTileSize ? 'none' : '1px solid rgb(238,238,238)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignContent: 'center',
            height: '100%',
          }}
        >
          <div
            style={{
              margin: 'auto',
              border:
                chartArea === 'dashboard'
                  ? 'none'
                  : `${chartControl.cardControls.borderTickness}px ${chartControl.cardControls.dashStyle} ${chartControl.cardControls.borderColor}`,
              borderRadius: `${chartControl.cardControls.borderRadius}px`,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignContent: 'center',
              backgroundColor: chartControl.cardControls.manualColor
                ? chartControl.cardControls.bgColor
                : // : chartThemes[0].background,
                  chartArea
                  ? '#00000000'
                  : selectedTheme?.background,

              // height:
              //   chartControl.cardControls.height < graphDimension.height
              //     ? `${chartControl.cardControls.height}px`
              //     : graphDimension.height,
              // width:
              //   chartControl.cardControls.width < graphDimension.width
              //     ? `${chartControl.cardControls.width}px`
              //     : graphDimension.width,
              width:
                tabtileProps.showDash || !tileState.tiles[propKey]?.graphSizeFull
                  ? graphDimension.width
                  : graphDimension.width / 3,
              height:
                tabtileProps.showDash || !tileState.tiles[propKey]?.graphSizeFull
                  ? graphDimension.height
                  : graphDimension.height / 2.5,
              overflow: 'hidden',
              fontFamily: fontFamily,
              fontStyle: ` ${chartControl.cardControls.fontStyle}`,
            }}
          >
            {chartData.length >= 1 ? (
              <>
                {tabtileProps.showDash || true ? ( ///////// Remove true for dragging purpose
                  <>
                    <span>
                      <span
                        // title="Drag to change position"
                        style={{
                          fontFamily: fontFamily,
                          fontSize: chartControl.cardControls.fontSize
                            ? `${chartControl.cardControls.fontSize}px`
                            : // : `${graphDimension.width / 10}px`,
                              tabtileProps.showDash || !tileState.tiles[propKey]?.graphSizeFull
                              ? Math.min(graphDimension.width, graphDimension.height) / 10 + 15
                              : Math.min(graphDimension.width, graphDimension.height) / 30 + 15,
                          color: fontColor
                            ? fontColor
                            : chartControl.cardControls.manualColor
                              ? chartControl.cardControls.valueColor
                              : selectedTheme?.colors[0],
                          margin: '5px',
                          backgroundColor: backgroundColorValue
                            ? backgroundColorValue
                            : chartControl.cardControls.manualColor
                              ? chartControl.cardControls.bgColor
                              : // : chartThemes[0].background,
                                chartArea
                                ? '#00000000'
                                : selectedTheme?.background,

                          fontStyle: italicText ? italicText : 'none',
                          textDecoration: textUnderline ? textUnderline : 'none',
                          fontWeight: boldText ? boldText : 'none',
                          padding: '0px 5px',
                          borderRadius: '4px',
                        }}
                      >
                        {getFormatedChartData()}
                      </span>
                    </span>
                    <span>
                      <p
                        // title="Drag to change position"
                        style={{
                          fontFamily: fontFamily,
                          fontSize: chartControl.cardControls.subtextFontSize
                            ? `${chartControl.cardControls.subtextFontSize}px`
                            : tabtileProps.showDash || !tileState.tiles[propKey]?.graphSizeFull
                              ? Math.min(graphDimension.width, graphDimension.height) / 20 + 7
                              : Math.min(graphDimension.width, graphDimension.height) / 60 + 7,
                          color: chartControl.cardControls.manualColor
                            ? chartControls.properties[propKey].cardControls.labelColor
                            : selectedTheme?.dark
                              ? '#ffffff'
                              : '#808080',
                          margin: '5px',
                        }}
                      >
                        {chartControls.properties[propKey].cardControls.subText}
                      </p>
                    </span>
                  </>
                ) : (
                  <></>
                  /// Uncomment for custom positioning using dragging
                  // <>
                  //   <Rnd
                  //     disableResizing={true}
                  //     disableDragging={chartArea === "dashboard" ? true : false}
                  //     bounds="parent"
                  //     position={chartControl.cardControls.mainTextPos}
                  //     onDragStop={(e, d) => {
                  //       updateCardControls(propKey, "mainTextPos", {
                  //         x: d.x,
                  //         y: d.y,
                  //       });
                  //     }}
                  //     style={{
                  //       overflow: "hidden",
                  //     }}
                  //   >
                  //     <p
                  //       title="Drag to change position"
                  //       style={{
                  //         cursor: "move",
                  //         fontSize: chartControls.properties[propKey]
                  //           .cardControls.customStyle
                  //           ? `${chartControl.cardControls.fontSize}px`
                  //           : // : `${graphDimension.width / 10}px`,
                  //             Math.min(
                  //               graphDimension.width,
                  //               graphDimension.height
                  //             ) /
                  //               10 +
                  //             15,
                  //         color: fontColor
                  //           ? fontColor
                  //           : chartControls.properties[propKey].cardControls
                  //               .valueColor,
                  //         margin: "5px",
                  //         backgroundColor: backgroundColorValue
                  //           ? backgroundColorValue
                  //           : chartControls.properties[propKey].cardControls
                  //               .bgColor,
                  //         fontStyle: italicText ? italicText : "none",
                  //         textDecoration: textUnderline
                  //           ? textUnderline
                  //           : "none",
                  //         fontWeight: boldText ? boldText : "none",
                  //         padding: "0px 5px",
                  //         borderRadius: "4px",
                  //       }}
                  //     >
                  //       {getFormatedChartData()}
                  //     </p>
                  //   </Rnd>
                  //   <Rnd
                  //     disableResizing={true}
                  //     disableDragging={chartArea === "dashboard" ? true : false}
                  //     bounds="parent"
                  //     position={chartControl.cardControls.subTextPos}
                  //     onDragStop={(e, d) => {
                  //       updateCardControls(propKey, "subTextPos", {
                  //         x: d.x,
                  //         y: d.y,
                  //       });
                  //     }}
                  //   >
                  //     <p
                  //       title="Drag to change position"
                  //       style={{
                  //         cursor: "move",
                  //         fontSize: chartControls.properties[propKey]
                  //           .cardControls.customStyle
                  //           ? `${chartControl.cardControls.subtextFontSize}px`
                  //           : Math.min(
                  //               graphDimension.width,
                  //               graphDimension.height
                  //             ) /
                  //               20 +
                  //             7,
                  //         color:
                  //           chartControls.properties[propKey].cardControls
                  //             .labelColor,
                  //         margin: "5px",
                  //       }}
                  //     >
                  //       {chartControls.properties[propKey].cardControls.subText}
                  //     </p>
                  //   </Rnd>
                  // </>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const emptyQueryCondition =
    shouldChartDisplayWithSecurity(
      chartProperties?.properties[propKey]?.chartAxes,
      chartProperties?.properties[propKey]?.chartType,
      chartData,
    ) === false || chartData.length < 1;

  return emptyQueryCondition ? (
    <EmptyQuery
      graphDimension={graphDimension}
      propKey={propKey}
      chartArea={chartArea}
      graphTileSize={graphTileSize}
      chartThemes={chartThemes}
      showEmptyQueryMessageWithSecurity={
        !shouldChartDisplayWithSecurity(
          chartProperties?.properties[propKey]?.chartAxes,
          chartProperties?.properties[propKey]?.chartType,
          chartData,
        )
          ? true
          : false
      }
    />
  ) : (
    <RenderChart />
  );
};
const mapStateToProps = (state: any, ownProps: any) => {
  return {
    chartControls: state.chartControls,
    chartProperties: state.chartProperties,
    tabtileProps: state.tabTileProps,
    pageSettings: state.pageSettings,
    tileState: state.tileState,
  };
};
const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    updateCardControls: (propKey: string, option: string, value: any) =>
      dispatch(updateCardControls(propKey, option, value)),

    addOrEditSimplecardConditionalFormat: (propKey: string, value: any) =>
      dispatch(addOrEditSimplecardConditionalFormat(propKey, value)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SimpleCard);
// eslint-disable-next-line no-lone-blocks
{
  /* <span
                  style={{
                    fontSize: `${chartControl.cardControls.fontSize}px`,
                    color: chartThemes[0].colors[0],
                  }}
                >
                  {getFormatedChartData()}
                </span>
                <span
                  style={{
                    fontSize: `${chartControl.cardControls.subtextFontSize}px`,
                    color: chartThemes[0].colors[1],
                  }}
                >
                  {Object.keys(chartData[0])[0]}
                </span> */
}
